import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import { Grid, Typography, Tooltip, Alert, Autocomplete, TextField } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid";
import {
    Delete as DeleteIcon,
    Edit as EditIcon
} from "@mui/icons-material";
import { StyledBottomButton, StyledDataGrid } from './styles';

//Components
import RouterBreadcrumbs from "../../components/Breadcrumbs/RouterBreadcrumbs";
import Title from "../../components/Typography/Title";
import ConfirmationDialog from "../../components/Dialog/ConfirmationDialog";
import InformationDialog from '../../components/Dialog/InformationDialog';
import Texas100RoadSegmentsMap from '../../components/Map/Texas100RoadSegmentsMap';

//custom hook
import { useFetch } from "../../hooks/useFetch";

//constants
import { API_URLS } from "../../Constants";

//context
import { useConnectedProjectsData } from '../../context/ConnectedProjectsDataContext';

const pathItems = [
    "Dashboard",
    "Edit Roadway"
]
export default function ConnectedProjects() {
    const { connectedProjectsData, updateConnectedProjectsData } = useConnectedProjectsData();
    const [informationDialog, setInformationDialog] = useState({ open: false, title: "", message: "" });
    const navigate = useNavigate();
    const location = useLocation();
    const [selectedRoadway] = useState(() => location.state?.selectedRoadway);
    const [projectsData, setProjectsData] = useState([]);
    const [projectsListDropDown, setProjectsListDropDown] = useState([]);
    const projectsFetcher = useFetch("get", API_URLS["API_GET_PROJECTS"]);
    const roadwayFetcher = useFetch("get", API_URLS["GET_TEXAS100_MONITORED_ROADWAYS"]);

    //React Hooks
    const [selectedRoadwayCoordinates, setSelectedRoadwayCoordinates] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [connectedProjects, setConnectedProjects] = useState(connectedProjectsData);
    const [confirmationDialog, setConfirmationDialog] = useState({
        open: false,
        title: "",
        message: "",
        onConfirm: () => { },
    });

    //Fetch data from API when the component renders.
    useEffect(() => {

        //if no selected roadway, navigate to view roadways page.
        if (!selectedRoadway || selectedRoadway.length === 0) {
            navigate(`/view-roadways`);
            return;
        }

        //fetch all projects for drop down
        projectsFetcher.setRequest({});

        const roadwayUrl = `${API_URLS["GET_TEXAS100_MONITORED_ROADWAYS"]}?minRank=${selectedRoadway[0].rank}&maxRank=${selectedRoadway[0].rank}`;
        roadwayFetcher.setRequest({ passedUrl: roadwayUrl });
    }, []);

    //Once project response is received, set appropriate state variables.
    useEffect(() => {
        const response = projectsFetcher.serverResponse;
        if (response && response.length > 0) {

            // Sort the projects alphabetically by name
            const sortedProjects = response.toSorted((a, b) => a.projectTitle?.localeCompare(b.projectTitle));

            //Set projects state
            setProjectsData(sortedProjects);

            //Filter active and inactive projects
            const activeInactiveProjects = sortedProjects.filter((project) => {
                return (project.status.toLowerCase() === "active" || project.status.toLowerCase() === "inactive")
                    && (project.projectType.toLowerCase() !== "regional" && project.projectType.toLowerCase() !== "regional transit");
            }).map((project) => ({
                id: project.id,
                title: project.projectTitle,
                projectStatus: project.status
            }));

            //set projects drop down
            setProjectsListDropDown(activeInactiveProjects);
        }
    }, [projectsFetcher.serverResponse]);

    //Once roadway response is received, set appropriate state variables.
    useEffect(() => {
        if (roadwayFetcher?.serverResponse !== null) {
            setSelectedRoadwayCoordinates(roadwayFetcher.serverResponse);
        }
    }, [roadwayFetcher.serverResponse]);

    //If local conenxtedProjects change then change it in the context as well.
    useEffect(() => {
        updateConnectedProjectsData(connectedProjects);
    }, [connectedProjects]);

    //Set columns.
    const columns = [
        {
            field: 'projectTitle', headerName: 'Project Title', width: 400, renderCell: (params) => (
                <Typography variant="body2">
                    {params.row.projectTitle}
                </Typography>
            )
        },
        {
            field: 'projectType', headerName: 'Project Type', width: 200, renderCell: (params) => (
                <Typography variant="body2">
                    {params.row.projectType}
                </Typography>
            )
        },
        {
            field: 'impactArea',
            headerName: 'Impact Area',
            width: 250,
            renderCell: (params) => (
                <select
                    value={params.row.impactArea}
                    onChange={(e) => handleImpactAreaChange(params.row, e.target.value)}
                    aria-label={`Impact Area for road segment connected project ${params.row.projectTitle}`}
                >
                    <option value="None">Select impact area</option>
                    <option value="On Roadway">On Roadway</option>
                    <option value="Parallel">Parallel</option>
                    <option value="Perpendicular">Perpendicular</option>
                    <option value="Before/After Roadway">Before/After Roadway</option>

                </select>
            )
        },
        {
            field: 'actions',
            headerName: 'Actions',
            type: 'actions',
            width: 150,
            getActions: (params) => {
                const actions = [];
                const status = params.row.projectStatus.toLowerCase();
                const projectName = params.row.projectTitle;

                if (status === "archived") {
                    actions.push(
                        <GridActionsCellItem
                            icon={<Typography>View</Typography>}
                            label={`View ${projectName} details`}
                            onClick={() => navigate(`/review-project`, { state: { projectId: params.row.id, component: "View" } })}
                        />
                    );
                }
                else {
                    actions.push(
                        <GridActionsCellItem
                            icon={
                                <Tooltip title="Edit project" placement='right'>
                                    <EditIcon />
                                </Tooltip>
                            }
                            label={`Edit ${projectName}`}
                            onClick={() => navigate(`/edit-project?id=${params.row.id}`)}
                        />,
                        <GridActionsCellItem
                            icon={
                                <Tooltip title="Remove project from selected road segment" placement='right'>
                                    <DeleteIcon />
                                </Tooltip>
                            }
                            label={`Delete ${projectName} from selected road segment`}
                            onClick={() => handleDeleteSelected([params.row.id])}
                        />
                    );
                }
                return actions;
            }
        }
    ];

    //Invoked when "Select Projects" drop down is changed.
    const handleProjectsDropdownChange = (event) => {
        const selectedProjectId = event.target.value;
        const selectedProject = projectsData.filter((project) => project.id === selectedProjectId);
        if (selectedProject) {

            // Check for duplicates before adding the selected project.
            const isDuplicate = connectedProjects.some(
                (project) => project.id === selectedProjectId
            );
            if (!isDuplicate) {

                // If not a duplicate, add the selected project to the data table.
                setConnectedProjects((prevTableData) => {
                    const connectedProject = selectedProject.map((project) => {
                        return {
                            id: project.id,
                            projectTitle: project.projectTitle,
                            projectType: project.projectType,
                            impactArea: "None",
                            projectStatus: project.status
                        }
                    })
                    const newData = [...prevTableData, ...connectedProject];
                    return newData;
                });
            }
        }
    }

    const handleProjectAutocompleteChange = useCallback((event, newValue) => {
        if (newValue?.id) {
            handleProjectsDropdownChange({ target: { value: newValue.id } });
        }
    }, [handleProjectsDropdownChange]);

    //Invoked when "Impact Area" drop down is changed.
    function handleImpactAreaChange(selectedRow, newImpactArea) {

        selectedRow.impactArea = newImpactArea;

        // Update the impact area in the connectedProjects state based on the selected value.
        setConnectedProjects((prevConnectedProjects) =>
            prevConnectedProjects.map((row) =>
                row.id === selectedRow.id ? { ...row, impactArea: newImpactArea } : row
            )
        );
    }

    //Connected Project Deletion: Set action on clicking "Yes" for selected project.
    const handleDeleteConfirm = () => {
        setConfirmationDialog((prevState) => ({ ...prevState, open: false }));
    };

    //Connected Project Deletion: Set ConfirmationDialog properties for selected project.
    const handleDeleteSelected = (selectedIds) => {
        if (selectedIds && selectedIds.length > 0) {
            setConfirmationDialog({
                open: true,
                title: "Delete Confirmation",
                message: `Are you sure you want to delete the selected project ?`,
                onConfirm: () => {
                    const updatedConnectedProjects = connectedProjects.filter((row) => !selectedIds.includes(row.id));

                    //Set table data with updated data.
                    setConnectedProjects(updatedConnectedProjects);

                    setSelectedRows([]);
                    handleDeleteConfirm();
                }
            },
            );
        }
    };

    const handleSaveBtn = () => {

        //check if any impact area is empty
        const projectsWithNoneImpact = connectedProjects.filter(proj => proj.impactArea.toLowerCase() === "none");
        if (projectsWithNoneImpact.length === 0) {
            //Update the context
            updateConnectedProjectsData(connectedProjects);

            //Navigate back to edit roadway page
            navigate(`/edit-roadway`, { state: { segmentId: selectedRoadway[0].segmentId, navFromView: false } })
        }
        else {
            setInformationDialog({
                open: true,
                title: "Information Dialog",
                message: `Impact area cannot be empty. Choose an impact area.`,
            });
        }
    };

    return (
        <>
            {/* Breadcrumbs for the page */}
            <Grid item xs={12}>
                <RouterBreadcrumbs pathItems={pathItems} />
            </Grid>

            {/* Page Title for the page */}
            <Grid item xs={12}>
                <Title title="Add Connected Projects" />
            </Grid>

            {/* Roadway name */}
            {selectedRoadway && selectedRoadway.length > 0 && (
                <Grid item xs={12}>
                    <Typography variant="h6" component="h3" gutterBottom>
                        <strong>Roadway Name: </strong>
                        {selectedRoadway[0].roadwayTitle}
                    </Typography>
                </Grid>
            )}

            {/* Dropdown to select project */}
            {projectsListDropDown !== null && (
                <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2, marginRight: "10px" }}>
                    <Autocomplete
                        id="project-autocomplete"
                        options={projectsListDropDown}
                        getOptionLabel={(option) => option.title || ""}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        onChange={handleProjectAutocompleteChange}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Search projects to add to the roadway"
                                variant="outlined"
                                inputProps={{
                                    ...params.inputProps,
                                    autoComplete: 'new-password',
                                }}
                                sx={{
                                    "& .MuiInputBase-input::placeholder": {
                                        color: "black",
                                        opacity: 1,
                                    },
                                }}
                            />
                        )}
                        fullWidth
                    />
                </Grid>
            )}

            {/* Map displaying the selected roadway */}
            <Grid item xs={12}>
                <Texas100RoadSegmentsMap
                    data={selectedRoadwayCoordinates}
                    isLoading={selectedRoadwayCoordinates.length === 0}
                    mapStyle={{ marginRight: "10px" }}
                    showAutocomplete={false}
                    interactive={false}
                    ariaLabel={selectedRoadway?.[0]?.roadwayTitle
                        ? `Map showing road ${selectedRoadway[0].roadwayTitle}`
                        : "Roadway Map"}
                />
            </Grid>

            {/* Alert when no data in table */}
            {connectedProjects.length === 0 && (
                <>
                    <Grid item xs={12}>
                        <Alert aria-label="No data to display. Select project from drop down to add." style={{ marginBottom: '10px', marginRight: "10px" }} severity="error">No data to display. Select project from drop down to add.</Alert>
                    </Grid>
                    <Grid item xs={12} style={{ display: "flex", justifyContent: "center" }}>
                        <StyledBottomButton aria-label="Back Button" variant="contained" color="primary" onClick={() => { navigate(`/edit-roadway`, { state: { segmentId: selectedRoadway[0].segmentId, navFromView: false } }) }}>
                            Back
                        </StyledBottomButton>
                    </Grid>
                </>
            )}

            {/* DataGrid containing Connected Projects information */}
            {connectedProjects != null && connectedProjects.length > 0 && (
                <>
                    <Grid item xs={12} style={{ marginRight: 10 }}>
                        <Typography aria-label="Connected Projects Heading" sx={{ fontWeight: "bold", marginBottom: "10px", marginTop: "25px" }}> Connected Projects</Typography>
                        <StyledDataGrid
                            aria-label="Connected Projects Table. Use arrow keys to navigate the table."
                            disableRowSelectionOnClick
                            rows={connectedProjects.slice().sort((a, b) => {
                                if (a.impactArea === "On Roadway" && b.impactArea !== "On Roadway")
                                    return -1;
                                else if (a.impactArea !== "On Roadway" && b.impactArea === "On Roadway")
                                    return 1;
                                else
                                    return 0;
                            })}
                            columns={columns}
                            rowKey={(row) => row.id}
                            style={{ width: '100%' }}
                            autoHeight
                            slotProps={{
                                pagination: {
                                    labelRowsPerPage: 'Rows per page:',
                                    SelectProps: {
                                        native: true,
                                        inputProps: {
                                            'aria-label': 'Rows per page',
                                            'id': 'pagination-select-input'
                                        }
                                    }
                                },
                            }}
                            initialState={{
                                pagination: {
                                    paginationModel: {
                                        page: 0,
                                        pageSize: 5,
                                    },
                                },
                            }}
                            pageSizeOptions={[5, 10, 15, 20]}
                            onRowSelectionModelChange={(newRowSelectionModel) => {
                                setSelectedRows(newRowSelectionModel);
                            }}
                            selectedRows={selectedRows}
                        />
                    </Grid>
                    <Grid item xs={12} style={{ display: "flex", justifyContent: "center" }}>
                        <StyledBottomButton aria-label="Save changes to connected projects" variant="contained" color="primary" onClick={handleSaveBtn}>
                            Save
                        </StyledBottomButton>
                    </Grid>
                </>
            )}

            <ConfirmationDialog
                open={confirmationDialog.open}
                onClose={() => setConfirmationDialog((prevState) => ({ ...prevState, open: false }))}
                title={confirmationDialog.title}
                message={confirmationDialog.message}
                onConfirm={confirmationDialog.onConfirm}
            />

            {/* Information Dialog component */}
            <InformationDialog
                open={informationDialog.open}
                onClose={() =>
                    setInformationDialog((prevState) => ({ ...prevState, open: false }))
                }
                title={informationDialog.title}
                message={informationDialog.message}
                onConfirm={informationDialog.onConfirm}
            />
        </>
    );
}

